/**
 * Combat State Definitions
 *
 * Static data defining all combat states (conditions, stances, talent-states)
 * derived from RT Core Ch09. These definitions are the single source of truth
 * for state behavior — UI and modifier injection both read from here.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CombatStateCategory = 'condition' | 'stance' | 'talent-state';

export interface CombatStateModifier {
  target: string; // "Ballistic Skill", "Weapon Skill", "Dodge", "all", etc.
  value: number; // +10, -20, etc.
  description: string; // "Pinned: -20 BS"
}

export interface CombatStateDefinition {
  id: string;
  name: string;
  description: string;
  icon: string; // emoji
  category: CombatStateCategory;
  modifiers: CombatStateModifier[]; // auto-injected into rolls
  actionRestrictions: string[]; // e.g., ["no-actions", "no-reactions", "half-actions-only"]
  reminders: string[]; // displayed in UI as rule reminders
  suppressedBy: string[]; // state IDs that suppress this state's effects
  requiresTalent?: string; // talent ID required to activate (e.g., "frenzy")
  suppresses?: string[]; // other state effects this state suppresses
}

export interface ActiveCombatState {
  id: string; // References CombatStateDefinition.id
  active: boolean;
  roundsRemaining: number | null; // null = indefinite, 0 = expired
  source?: string; // "Critical Damage to Head", "Suppressing Fire", etc.
  activatedAt: number; // timestamp for ordering
}

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

export const COMBAT_STATE_DEFINITIONS: CombatStateDefinition[] = [
  {
    id: 'stunned',
    name: 'Stunned',
    description: 'Cannot take Actions or Reactions. Attackers get +20 to hit.',
    icon: '💫',
    category: 'condition',
    modifiers: [], // Stunned doesn't penalize the stunned character's rolls — it benefits attackers
    actionRestrictions: ['no-actions', 'no-reactions'],
    reminders: [
      'Cannot take Actions or Reactions (including Dodge)',
      'Attackers get +20 to WS/BS Tests to hit you',
      'You are not helpless or unaware'
    ],
    suppressedBy: ['frenzied', 'duty-unto-death'],
    suppresses: []
  },
  {
    id: 'pinned',
    name: 'Pinned',
    description: 'Half Actions only. -20 BS. Must stay in or reach cover.',
    icon: '📌',
    category: 'condition',
    modifiers: [{ target: 'Ballistic Skill', value: -20, description: 'Pinned: -20 BS' }],
    actionRestrictions: ['half-actions-only'],
    reminders: [
      'May only take Half Actions',
      'Must stay in cover or move to cover',
      'WP Test at end of turn to escape (Easy +30 if no longer under fire)',
      'Melee engagement automatically escapes Pinning'
    ],
    suppressedBy: ['frenzied'],
    suppresses: []
  },
  {
    id: 'on-fire',
    name: 'On Fire',
    description: '1d10 Energy Damage (ignores armour) + 1 Fatigue each Round.',
    icon: '🔥',
    category: 'condition',
    modifiers: [],
    actionRestrictions: [],
    reminders: [
      '1d10 Energy Damage (ignores armour) + 1 Fatigue each Round',
      'WP Test at start of turn or can only run and scream (Full Action)',
      'Extinguish: drop Prone + Hard (-20) Agility Test (Full Action)'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'bleeding',
    name: 'Blood Loss',
    description: '10% chance of death each Round unless treated.',
    icon: '🩸',
    category: 'condition',
    modifiers: [],
    actionRestrictions: [],
    reminders: [
      '10% chance of death each Round unless treated',
      'Conscious: Difficult (-10) Medicae Test each Round to staunch',
      'If also in strenuous activity: Very Hard (-30) Medicae Test instead'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'prone',
    name: 'Prone',
    description: 'Melee attackers get +10. Ranged attackers get -10. Dodge at -20.',
    icon: '⬇️',
    category: 'stance',
    modifiers: [{ target: 'Dodge', value: -20, description: 'Prone: -20 Dodge' }],
    actionRestrictions: [],
    reminders: [
      'Melee attackers get +10 to hit you',
      'Ranged attackers get -10 to hit you',
      'Stand up requires a Half Action'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'unconscious',
    name: 'Unconscious',
    description: 'Completely unaware. Cannot take any actions. Treated as helpless.',
    icon: '😵',
    category: 'condition',
    modifiers: [],
    actionRestrictions: ['no-actions', 'no-reactions'],
    reminders: [
      'Completely unaware of surroundings',
      'Cannot take any actions',
      'Treated as a helpless target (auto-hit in melee, double damage dice)'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'grappling',
    name: 'Grappling',
    description: 'Cannot use Reactions. Can only use Grapple Action.',
    icon: '🤼',
    category: 'condition',
    modifiers: [],
    actionRestrictions: ['no-reactions', 'grapple-only'],
    reminders: [
      'Cannot use Reactions',
      'Can only use the Grapple Action',
      'Other attackers get +20 WS to hit you',
      'Controller can end Grapple as Free Action'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'helpless',
    name: 'Helpless',
    description: 'Auto-hit in melee. Double damage dice.',
    icon: '🆘',
    category: 'condition',
    modifiers: [],
    actionRestrictions: ['no-actions', 'no-reactions'],
    reminders: [
      'Melee attacks automatically succeed',
      'Damage dice rolled twice and added together',
      'If two dice show 10, Righteous Fury is automatic'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'blinded',
    name: 'Blinded',
    description: 'Auto-fail vision tests and BS Tests. -30 WS.',
    icon: '🙈',
    category: 'condition',
    modifiers: [
      { target: 'Weapon Skill', value: -30, description: 'Blinded: -30 WS' },
      { target: 'Ballistic Skill', value: -100, description: 'Blinded: auto-fail BS' }
    ],
    actionRestrictions: [],
    reminders: [
      'Automatically fail all Ballistic Skill Tests',
      '-30 to Weapon Skill Tests',
      'Automatically fail all tests based on vision'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'deafened',
    name: 'Deafened',
    description: 'Auto-fail hearing-based tests.',
    icon: '🔇',
    category: 'condition',
    modifiers: [],
    actionRestrictions: [],
    reminders: [
      'Automatically fail any test relying on hearing',
      'Cannot communicate verbally with allies'
    ],
    suppressedBy: [],
    suppresses: []
  },
  {
    id: 'frenzied',
    name: 'Frenzied',
    description: '+10 WS/S/T/WP, -20 BS/Int. Immune to Fear, Pinning, Stun, Fatigue effects.',
    icon: '😤',
    category: 'talent-state',
    modifiers: [
      { target: 'Weapon Skill', value: 10, description: 'Frenzied: +10 WS' },
      { target: 'Strength', value: 10, description: 'Frenzied: +10 S' },
      { target: 'Toughness', value: 10, description: 'Frenzied: +10 T' },
      { target: 'Willpower', value: 10, description: 'Frenzied: +10 WP' },
      { target: 'Ballistic Skill', value: -20, description: 'Frenzied: -20 BS' },
      { target: 'Intelligence', value: -20, description: 'Frenzied: -20 Int' }
    ],
    actionRestrictions: ['must-all-out-attack', 'no-parry', 'no-retreat'],
    reminders: [
      'Must use All-Out Attack in melee if possible',
      'Cannot Parry, retreat, or flee',
      'Immune to Fear, Pinning, Stunning, and Fatigue effects',
      'Must attack nearest enemy in melee if possible',
      'Lasts for duration of combat'
    ],
    suppressedBy: [],
    requiresTalent: 'frenzy',
    suppresses: ['stunned', 'pinned', 'fatigue']
  },
  {
    id: 'duty-unto-death',
    name: 'Duty Unto Death',
    description: 'Ignore injury effects, Fatigue, and Stunning during combat.',
    icon: '⚔️',
    category: 'talent-state',
    modifiers: [],
    actionRestrictions: [],
    reminders: [
      'Ignore effects of injury, Fatigue, and Stunning',
      'Damage is still taken — only effects are suppressed',
      'Death still affects you normally',
      'Lasts for duration of combat'
    ],
    suppressedBy: [],
    requiresTalent: 'duty-unto-death',
    suppresses: ['stunned', 'fatigue']
  }
];

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/**
 * Look up a combat state definition by its kebab-case ID.
 * Returns `undefined` if the ID is not found.
 */
export const getStateDefinition = (id: string): CombatStateDefinition | undefined =>
  COMBAT_STATE_DEFINITIONS.find((def) => def.id === id);
